import * as Select from '@studio/design-system/select';
import {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryCommandKey,
  DeviceRegistryInterfaceItemControlType
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import type { ReactElement } from 'react';

import type { Proxy } from '#~/agent/index.js';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Control } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { SettingsStatus } from './status-notifications.js';
import { STATUS_NOTIFICATIONS } from './status-notifications.js';
import type { Device } from '@studio/models/device';

interface DeviceSettingSelectProps {
  agent?: T_Maybe<Proxy>;
  className: string;
  cmdKey: DeviceRegistryCommandKey;
  defaultValue: T_Maybe<number>;
  deviceId: Device['id'];
  disabled?: boolean;
  items: { disabled: boolean; label: string; value: number }[];
  label: string;
  onMutation: T_Maybe<(mutation: DeepPartial<Control>) => void>;
  onReactiveMutation?: T_Maybe<(mutation: DeepPartial<Control>) => Promise<void>>;
  value: number;
  onControlClick?: (controlData: {
    label: string;
    controlType: DeviceRegistryInterfaceItemControlType;
    key: DeviceRegistryCommandKey;
    value?: DeviceRegistryCommandFunctionType;
  }) => void;
  status?: SettingsStatus;
}

/** `<DeviceSettingSelect>` component. */
export const DeviceSettingSelect = ({
  agent,
  className,
  cmdKey,
  defaultValue,
  deviceId,
  disabled,
  items,
  label,
  onMutation,
  onReactiveMutation,
  value,
  onControlClick,
  status
}: DeviceSettingSelectProps): ReactElement => {
  const mutateSelectOption = async (option: string): Promise<void> => {
    const control: DeepPartial<Control> = {
      device: {
        [deviceId]: {
          deviceSettings: {
            [String(cmdKey)]: {
              key: cmdKey,
              lastModified: Date.now(),
              select: Number.parseInt(option, 10)
            }
          }
        }
      }
    };

    // Use reactive mutation handler if available, otherwise fall back to legacy behavior
    if (onReactiveMutation) {
      await onReactiveMutation(control);
    } else if (onMutation) {
      onMutation(control);
    } else {
      await agent?.mutate({ control }, {}).catch(noop);
    }

    onControlClick?.({
      label,
      controlType: DeviceRegistryInterfaceItemControlType.SELECT,
      key: cmdKey,
      value: DeviceRegistryCommandFunctionType.VALUE_SET
    });
  };

  return (
    <Select.Container
      canRevert={!disabled && defaultValue !== undefined && value !== defaultValue}
      className={className}
      label={label}
      onRevert={(): void => {
        mutateSelectOption(String(defaultValue!));
      }}
      status={status}
      notification={status ? STATUS_NOTIFICATIONS[status] : undefined}
    >
      <Select.Root disabled={disabled} onValueChange={mutateSelectOption} value={String(value)}>
        <Select.Trigger className='w-full'>
          <Select.Value />
        </Select.Trigger>

        <Select.Content>
          {items.map(({ disabled: optionDisabled, label: optionLabel, value: optionValue }) => (
            <Select.Item disabled={optionDisabled} key={optionValue} value={String(optionValue)}>
              {optionLabel}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    </Select.Container>
  );
};
